export type AiProviderMetadata = {
  providerId: string;
  providerGroupId: string;
  familyLabel: string;
  label: string;
  authMode: "api_key" | "oauth";
  description: string;
};

export const KNOWN_AI_PROVIDERS: AiProviderMetadata[] = [
  {
    providerId: "azure-openai",
    providerGroupId: "azure",
    familyLabel: "Azure OpenAI",
    label: "Azure OpenAI",
    authMode: "api_key",
    description: "Azure OpenAI credentials and deployment-backed model catalog.",
  },
  {
    providerId: "cohere",
    providerGroupId: "cohere",
    familyLabel: "Cohere",
    label: "Cohere API",
    authMode: "api_key",
    description: "Tenant-shared Cohere API key.",
  },
  {
    providerId: "google",
    providerGroupId: "google",
    familyLabel: "Google",
    label: "Google AI",
    authMode: "api_key",
    description: "Gemini and Google-hosted model access.",
  },
  {
    providerId: "mistral",
    providerGroupId: "mistral",
    familyLabel: "Mistral",
    label: "Mistral API",
    authMode: "api_key",
    description: "Tenant-shared Mistral credentials.",
  },
  {
    providerId: "nvidia",
    providerGroupId: "nvidia",
    familyLabel: "NVIDIA",
    label: "NVIDIA API",
    authMode: "api_key",
    description: "NVIDIA-hosted model access through the OpenAI-compatible API surface.",
  },
  {
    providerId: "openai",
    providerGroupId: "openai",
    familyLabel: "OpenAI",
    label: "OpenAI API key",
    authMode: "api_key",
    description: "API-key based OpenAI access for production agent traffic.",
  },
  {
    providerId: "openai-codex",
    providerGroupId: "openai",
    familyLabel: "OpenAI",
    label: "ChatGPT Subscription (OpenAI Codex)",
    authMode: "oauth",
    description: "ChatGPT subscription-backed OAuth profile using the existing openai-codex provider.",
  },
  {
    providerId: "openrouter",
    providerGroupId: "openrouter",
    familyLabel: "OpenRouter",
    label: "OpenRouter API",
    authMode: "api_key",
    description: "Unified catalog access through OpenRouter.",
  },
  {
    providerId: "xai",
    providerGroupId: "xai",
    familyLabel: "xAI",
    label: "xAI API",
    authMode: "api_key",
    description: "Tenant-shared xAI credentials.",
  },
];

function titleCase(value: string): string {
  return value
    .split(/[-_/.\s]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getAiProviderMetadata(providerId: string): AiProviderMetadata {
  const normalized = providerId.trim().toLowerCase();
  const known = KNOWN_AI_PROVIDERS.find((entry) => entry.providerId === normalized);
  if (known) {
    return known;
  }
  const groupId = normalized.includes("-") ? normalized.split("-")[0]! : normalized;
  return {
    providerId: normalized,
    providerGroupId: groupId,
    familyLabel: titleCase(groupId || "provider"),
    label: titleCase(normalized || "provider"),
    authMode: normalized === "openai-codex" ? "oauth" : "api_key",
    description: "Tenant-shared provider connection.",
  };
}

export function groupAiProviderMetadata(): Map<string, AiProviderMetadata[]> {
  const grouped = new Map<string, AiProviderMetadata[]>();
  for (const provider of KNOWN_AI_PROVIDERS) {
    const existing = grouped.get(provider.familyLabel) ?? [];
    existing.push(provider);
    grouped.set(provider.familyLabel, existing.toSorted((left, right) => left.label.localeCompare(right.label)));
  }
  return new Map([...grouped.entries()].toSorted((left, right) => left[0].localeCompare(right[0])));
}
